<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to HR Menu" @click="$router.push('/hr')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn fab small dark color="primary" @click="showAdd" title="Add eDocument">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                {{errorMessage}}
            </v-alert>
            <v-card class="elevation-1 mt-5">
                <v-toolbar flat height="45px">
                    <div :class="isMobile?'wp-100 mb-10':'wp-50 mb-10'">
                        <v-text-field v-model="search" append-icon="search" :label="searchLabel" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : header"
                    :items="edocumentsFiltered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >  
                    <template v-slot:items="props">
                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="header.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewDetails(props.item.documentId)">
                                    <v-toolbar flat height="42">
                                        <b>{{ props.item.documentTitle }}</b>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-1 mb-1">
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text">Description</div>
                                                    <div>{{ props.item.documentDescription }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text">Category</div>
                                                    <v-chip class="ml-0">
                                                        {{ props.item.categoryName }}
                                                    </v-chip>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text">Type</div>
                                                    <v-chip class="ml-0">
                                                        {{ props.item.typeName }}
                                                    </v-chip>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs6>
                                                    <div class="caption grey--text">Current Version</div>
                                                    <span v-if="props.item.currentVersionNumber==0">None</span>
                                                    <span v-else>{{ props.item.currentVersionNumber }}</span>
                                                </v-flex>
                                                <v-flex xs6>
                                                    <div class="caption grey--text">Created On</div>
                                                    <div>{{ props.item.createdOn | moment('MMM DD, YYYY') }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text">Created By</div>
                                                    <v-chip class="user-chip-container ml-0">
                                                        <v-avatar>
                                                            <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.createdBy.userId)"></v-img>
                                                        </v-avatar>
                                                        {{props.item.createdBy.firstName}} {{props.item.createdBy.lastName}}
                                                    </v-chip>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item.documentId)" title="Click to view/edit details" class="clickable">
                            <td>{{ props.item.documentTitle }}</td>
                            <td>{{ props.item.documentDescription }}</td>
                            <td>
                                <v-chip class="ml-0">
                                    {{ props.item.categoryName }} - {{ props.item.typeName }}
                                </v-chip>
                            </td>
                            <td>
                                <span v-if="props.item.currentVersionNumber==0">None</span>
                                <span v-else>{{ props.item.currentVersionNumber }}</span>
                            </td>
                            <td>
                                <v-chip class="user-chip-container ml-0">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.createdBy.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.createdBy.firstName}} {{props.item.createdBy.lastName}}
                                </v-chip>
                            </td>
                            <td>{{ props.item.createdOn | moment('MMM DD, YYYY') }}</td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogAddEditDocHeader" persistent width="550">
            <add-edit-header ref="refAddEditEDoc" :dialogAddEditDocHeader.sync="dialogAddEditDocHeader" @send="viewDetails"></add-edit-header>
        </v-dialog>
    </v-layout> 
</template>

<script>

import edocumentsApi from '@/api/edocuments'
import rowsPerPageItems from '@/constants/rows-per-page'
import awsS3Helper from '@/helpers/aws-s3'
import { getSort, getRowsPerPage, updateRowsPerPage, updateSort } from '@/helpers/user-prefs'

export default {     
    name: 'eDocuments',
    metaInfo: {
        title: 'eDocuments',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'eDocuments' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'add-edit-header': ()=> import("@/components/EDocuments/AddEditEDocumentHeader"),
    },
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            header: [
                { text: 'Title', align: 'left', value: 'documentTitle', width: '25%', sortable: true },
                { text: 'Description', align: 'left', value: 'documentDescription', width: '35%', sortable: true },
                { text: 'Category & Type', align: 'left', value: 'categoryName', width: '10%', sortable: true },
                { text: 'Versions', align: 'left', value: 'versionNumber', width: '10%', sortable: false },
                { text: 'Created By', align: 'left', value: 'lastName', width: '10%', sortable: true },
                { text: 'Created On', align: 'left', value: 'createdOn', width: '10%', sortable: true },
            ],
            search: null,
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEditDocHeader: false,
            edocuments: []
        }
    },
    methods: {
        showAdd() {
            this.$refs.refAddEditEDoc.setAdd()
            this.dialogAddEditDocHeader = true
        },
        viewDetails(documentId) {
            this.$router.push(`/hr/edocuments/${documentId}`)
        },
        async getEDocumentsSummary(show_loading) {
            try {
                this.loading = show_loading
                const response = await edocumentsApi.getSummary()
                if (response && response.status == 200) {
                    this.edocuments = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'eDocuments'
        })

        this.getEDocumentsSummary(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        searchLabel() {
            return this.isMobile ? 'Search' : 'Search Title, Description, Category, Type'
        },
        edocumentsFiltered() {
            let temp = this.edocuments

            // search handler
            if (this.search) {
                const searchLower = this.search.toLowerCase()

                temp = temp.filter(item => {
                    const titleMatch = item.documentTitle.toLowerCase().includes(searchLower)
                    const descriptionMatch = item.documentDescription.toLowerCase().includes(searchLower)
                    const categoryNameMatch = item.categoryName.toLowerCase().includes(searchLower)
                    const typeNameMatch = item.typeName.toLowerCase().includes(searchLower)
                   
                    return titleMatch || descriptionMatch || categoryNameMatch || typeNameMatch
                })
            }

            return temp
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>